import React from 'react';

import useGeneratePortalLink from '../../api/useGeneratePortalLink';
import { PortalLink as PortalLinkData } from '../../types';
import { formatDateTime } from '../../utils/formatters';
import * as CardStyle from '../../style.module.scss';
import * as Style from './style.module.scss';

type Props = {
    memberId: number | null | undefined;
    portalLink: PortalLinkData | null;
    isLoading: boolean;
};

const PortalLink: React.FC<Props> = ({ memberId, portalLink, isLoading }) => {
    const [copied, setCopied] = React.useState(false);
    const portalLinkFull =
        portalLink?.token &&
        `https://herohealth.com/polycare-consents/?form_fill_id=${portalLink.token}`;

    const { mutate: generateLink, isPending } = useGeneratePortalLink(memberId);

    React.useEffect(() => {
        if (!copied) {
            return undefined;
        }

        const timer = window.setTimeout(() => setCopied(false), 2000);

        return () => window.clearTimeout(timer);
    }, [copied]);

    const handleCopy = async () => {
        if (!portalLinkFull) {
            return;
        }

        try {
            await navigator.clipboard.writeText(portalLinkFull);
            setCopied(true);
        } catch {
            // Clipboard writes reject in insecure contexts and iframes; fail quietly
            // so the token stays selectable by hand.
        }
    };

    const generateAction = (
        <button
            type="button"
            className={CardStyle.linkButton}
            disabled={isPending}
            onClick={() => generateLink()}
        >
            {isPending ? 'Generating...' : portalLink ? 'Regenerate' : 'Generate'}
        </button>
    );

    const renderValue = () => {
        if (isLoading) {
            return <span className={CardStyle.muted}>Loading...</span>;
        }

        if (!portalLink) {
            return (
                <span className={CardStyle.muted}>
                    {'No active patient portal link · '}
                    {generateAction}
                </span>
            );
        }

        return (
            <>
                <div className={Style.tokenRow}>
                    <code className={Style.token}>{portalLinkFull}</code>
                    <button
                        type="button"
                        className={Style.copyButton}
                        onClick={handleCopy}
                        aria-label="Copy patient portal link to clipboard"
                    >
                        {copied ? 'Copied' : 'Copy'}
                    </button>
                </div>
                <span className={CardStyle.subText}>
                    {`Generated ${formatDateTime(portalLink.generated_at)} · ${
                        portalLink.last_accessed_at
                            ? `Last opened ${formatDateTime(portalLink.last_accessed_at)}`
                            : 'Never opened'
                    }${portalLink.is_active ? '' : ' · Inactive'} · `}
                    {generateAction}
                </span>
            </>
        );
    };

    return (
        <div className={`${CardStyle.field} ${CardStyle.fieldWide}`}>
            <span className={CardStyle.fieldLabel}>Patient portal link</span>
            {renderValue()}
        </div>
    );
};

export default PortalLink;
